import React, { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/Layout/Header';
import { Footer } from '../components/Layout/Footer';

export const PrivacyPolicyPage: React.FC = () => {
    const { user, loading } = useAuth();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-2 border-slate-200 border-t-blue-600 mb-4"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white min-h-screen flex flex-col h-screen overflow-y-auto">
            <Header
                activeAudience="recruiter"
                showLandingNav={!user}
            />

            <main className="flex-1 w-full max-w-4xl mx-auto px-4 md:px-6 py-24 md:py-32">
                <div className="prose prose-slate lg:prose-lg max-w-none">
                    <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
                    <p className="text-slate-500 mb-8">Last Updated: 08/11/2025</p>

                    <p className="text-slate-600 mb-6">
                        JobVital ('we', 'our', 'us') respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your information when you use our platform or apply for jobs.
                    </p>

                    <section className="mb-8">
                        <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-4">1. Who We Are</h2>
                        <p className="text-slate-600">
                            JobVital is an early-stage platform designed to connect jobseekers with employers. We sometimes post jobs on behalf of third-party hiring companies ('Clients'). When you apply to a job, we may share your details with the hiring company if you are a potential match.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-4">2. Information We Collect</h2>
                        <p className="text-slate-600 mb-3">When you sign up or apply through JobVital, we may collect:</p>
                        <ul className="list-disc pl-5 space-y-2 text-slate-600">
                            <li>Name and contact details (email address, phone number)</li>
                            <li>Uploaded CV/resume and application information</li>
                            <li>Country and role preferences</li>
                            <li>Education, experience, and skills</li>
                            <li>Any other details you voluntarily provide</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-4">3. How We Use Your Information</h2>
                        <p className="text-slate-600 mb-3">We use your data to:</p>
                        <ul className="list-disc pl-5 space-y-2 text-slate-600">
                            <li>Assess your suitability for roles</li>
                            <li>Share your details with hiring companies when appropriate</li>
                            <li>Send you other suitable job opportunities</li>
                            <li>Communicate with you about your account or updates</li>
                            <li>Improve our platform and matching system</li>
                        </ul>
                        <p className="text-slate-600 mt-4 font-medium">We will never sell your personal data.</p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-4">4. When We Share Your Information</h2>
                        <p className="text-slate-600 mb-3">We may share your data with:</p>
                        <ul className="list-disc pl-5 space-y-2 text-slate-600">
                            <li>Hiring companies that are advertising jobs through us</li>
                            <li>Service providers that help us run the platform (for hosting, email delivery, etc.)</li>
                            <li>Legal authorities if required by law</li>
                        </ul>
                        <p className="text-slate-600 mt-4">All partners are expected to handle your data securely and only for recruitment purposes.</p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-4">5. Data Retention & Deletion</h2>
                        <p className="text-slate-600">
                            You can delete your account at any time. When you do, all personal information, including your CV, profile, and application history, will be permanently deleted from our systems within 30 days.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-4">6. Your Rights</h2>
                        <p className="text-slate-600 mb-3">You can:</p>
                        <ul className="list-disc pl-5 space-y-2 text-slate-600">
                            <li>Access your data</li>
                            <li>Update or correct your details</li>
                            <li>Request permanent deletion of your account</li>
                            <li>Withdraw consent to receive emails or job alerts</li>
                        </ul>
                        <p className="text-slate-600 mt-4">
                            To exercise these rights, contact <a href="mailto:info@jobvital.co.uk" className="text-blue-600 hover:text-blue-800">info@jobvital.co.uk</a>
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-4">7. Security</h2>
                        <p className="text-slate-600">
                            We use secure systems and reasonable measures to protect your information from unauthorized access, disclosure, or loss.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-4">8. Updates</h2>
                        <p className="text-slate-600">
                            We may update this policy as we develop our platform. When we do, we'll change the 'Last Updated' date and post the new version.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-4">9. Contact</h2>
                        <p className="text-slate-600">
                            If you have any privacy questions, contact us at: <a href="mailto:info@jobvital.co.uk" className="text-blue-600 hover:text-blue-800">info@jobvital.co.uk</a>
                        </p>
                    </section>
                </div>
            </main>

            <Footer />
        </div>
    );
};
